/**
 * /payroll/employees/:employeeId/w4 — Employee W-4 standalone page.
 *
 * Shows the current W-4 on file and a form to submit a new one.
 *
 * GET /api/payroll/employees/{employeeId}/w4
 *   Returns: { tax_setup_id, work_state, effective_start,
 *              federal_w4: { filing_status, multiple_jobs?, dependents_amt?,
 *                            other_income?, deductions?, extra_withholding?,
 *                            exempt? } }
 *   404 if no W-4 exists yet.
 *
 * POST /api/payroll/employees/{employeeId}/w4
 *   Body: W4Input — filing_status, multiple_jobs_checked, dependents_amount,
 *         other_income, deductions, extra_withholding, exempt, work_state,
 *         effective_date, employer_name, employer_ein
 *   Returns W4Out: { tax_setup_id, document_id, document_url, effective_date }
 *
 * Note: each submission creates a new versioned TaxSetup row; prior W-4s
 * are preserved for audit per IRS Rev. Proc. 2017-72.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { MoneyField } from "@/components/MoneyField";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DownloadIcon from "@mui/icons-material/Download";
import { api } from "@/lib/api";
import { payrollKeys } from "@/lib/queries";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/employees_/$employeeId/w4")({
  component: EmployeeW4Page,
});

// ── Types matching the wire shapes ───────────────────────────────────────────

interface W4Current {
  tax_setup_id: string;
  work_state: string;
  effective_start: string;
  federal_w4: {
    filing_status: string;
    multiple_jobs?: boolean;
    dependents_amt?: string;
    other_income?: string;
    deductions?: string;
    extra_withholding?: string;
    exempt?: boolean;
  };
}

interface W4SubmitResult {
  tax_setup_id: string;
  document_id: string;
  document_url: string;
  effective_date: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

const FILING_STATUS_OPTIONS = [
  { value: "single", label: "Single / Married filing separately" },
  { value: "married_filing_jointly", label: "Married filing jointly" },
  { value: "head_of_household", label: "Head of household" },
];

const FILING_STATUS_LABEL: Record<string, string> = {
  single: "Single / Married filing separately",
  married_filing_jointly: "Married filing jointly",
  head_of_household: "Head of household",
};

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function ReadOnlyField({ label, value }: { label: string; value: string | undefined }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value ?? "—"}</Typography>
    </Box>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

function EmployeeW4Page() {
  const { employeeId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // ── Load current W-4 ─────────────────────────────────────────────────────
  const w4Q = useQuery<W4Current>({
    queryKey: ["payroll", "employees", employeeId, "w4"],
    queryFn: () => api<W4Current>(`/api/payroll/employees/${employeeId}/w4`),
    retry: false,
  });

  // ── Form state ────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    filing_status: "single",
    multiple_jobs_checked: false,
    dependents_amount: "0",
    other_income: "0",
    deductions: "0",
    extra_withholding: "0",
    exempt: false,
    work_state: "CA",
    effective_date: new Date().toISOString().split("T")[0],
    employer_name: "",
    employer_ein: "",
  });

  const set = (key: string) => (
    e: React.ChangeEvent<HTMLInputElement | { value: unknown }>,
  ) => setForm((f) => ({ ...f, [key]: (e.target as HTMLInputElement).value }));

  const [submitted, setSubmitted] = useState<W4SubmitResult | null>(null);

  // ── Submit mutation ───────────────────────────────────────────────────────
  const submitMut = useMutation({
    mutationFn: () =>
      api<W4SubmitResult>(`/api/payroll/employees/${employeeId}/w4`, {
        method: "POST",
        body: {
          filing_status: form.filing_status,
          multiple_jobs_checked: form.multiple_jobs_checked,
          dependents_amount: parseFloat(form.dependents_amount) || 0,
          other_income: parseFloat(form.other_income) || 0,
          deductions: parseFloat(form.deductions) || 0,
          extra_withholding: parseFloat(form.extra_withholding) || 0,
          exempt: form.exempt,
          work_state: form.work_state,
          effective_date: form.effective_date,
          employer_name: form.employer_name,
          employer_ein: form.employer_ein,
        },
      }),
    onSuccess: (data) => {
      setSubmitted(data);
      qc.invalidateQueries({ queryKey: ["payroll", "employees", employeeId, "w4"] });
      qc.invalidateQueries({ queryKey: payrollKeys.employee(employeeId) });
    },
  });

  const currentW4 = w4Q.data;
  const fw4 = currentW4?.federal_w4 ?? ({} as W4Current["federal_w4"]);

  return (
    <Box sx={{ p: 3, maxWidth: 640 }}>
      {/* Back nav */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate({ to: "/payroll/employees/$employeeId", params: { employeeId } })}
        sx={{ mb: 2, textTransform: "none" }}
      >
        Back to Employee
      </Button>

      {/* Page title */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Federal W-4
        </Typography>
        <HelpHint docPath="payroll-l1/w4-setup.md" label="W-4 setup help" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Employee's Withholding Certificate (2020+ format). Each submission is versioned;
        prior W-4s are preserved for audit per IRS Rev. Proc. 2017-72.
      </Typography>

      {/* ── Current W-4 on file ────────────────────────────────────────── */}
      {w4Q.isLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Loading current W-4…</Typography>
        </Box>
      ) : currentW4 ? (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <AssignmentTurnedInIcon color="success" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700} color="success.main">
              W-4 on file — effective {currentW4.effective_start}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            <ReadOnlyField
              label="Filing Status"
              value={FILING_STATUS_LABEL[fw4.filing_status] ?? fw4.filing_status}
            />
            <ReadOnlyField label="Work State" value={currentW4.work_state} />
            <ReadOnlyField
              label="Multiple Jobs"
              value={fw4.multiple_jobs ? "Yes" : "No"}
            />
            <ReadOnlyField
              label="Dependents ($, Step 3)"
              value={fw4.dependents_amt ? `$${fw4.dependents_amt}` : "$0"}
            />
            <ReadOnlyField
              label="Other Income ($, Step 4a)"
              value={fw4.other_income ? `$${fw4.other_income}` : "$0"}
            />
            <ReadOnlyField
              label="Deductions ($, Step 4b)"
              value={fw4.deductions ? `$${fw4.deductions}` : "$0"}
            />
            <ReadOnlyField
              label="Extra W/H ($/period)"
              value={fw4.extra_withholding ? `$${fw4.extra_withholding}` : "$0"}
            />
            <ReadOnlyField
              label="Exempt"
              value={fw4.exempt ? "Yes — no federal withholding" : "No"}
            />
          </Box>
        </Paper>
      ) : w4Q.isError ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No W-4 on file yet. Submit one below to enable federal tax withholding calculations.
        </Alert>
      ) : null}

      {/* ── Submit new W-4 form ────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {currentW4 ? "Update W-4" : "Submit W-4"} — Federal Form W-4 (2020+)
        </Typography>

        {/* Step 1 */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            STEP 1 — Personal Information
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel>Step 1c — Filing Status</InputLabel>
            <Select
              value={form.filing_status}
              label="Step 1c — Filing Status"
              onChange={(e) => setForm((f) => ({ ...f, filing_status: e.target.value }))}
            >
              {FILING_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Step 2 */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            STEP 2 — Multiple Jobs or Spouse Works
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={form.multiple_jobs_checked}
                onChange={(e) =>
                  setForm((f) => ({ ...f, multiple_jobs_checked: e.target.checked }))
                }
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Check box 2c — employee has multiple jobs or spouse also works
              </Typography>
            }
          />
        </Box>

        {/* Step 3 */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            STEP 3 — Claim Dependents
          </Typography>
          <MoneyField
            label="Qualifying dependents amount / yr"
            size="small"
            fullWidth
            value={form.dependents_amount}
            onChange={set("dependents_amount")}
            helperText="Total child tax credit + other dependent credits for the year (W-4 line 3)"
          />
        </Box>

        {/* Step 4 */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1, fontWeight: 600 }}
          >
            STEP 4 — Other Adjustments (optional)
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <MoneyField
              label="Step 4a — Other income / yr"
              size="small"
              value={form.other_income}
              onChange={set("other_income")}
              helperText="Non-wage income, e.g. interest or dividends"
            />
            <MoneyField
              label="Step 4b — Deductions / yr"
              size="small"
              value={form.deductions}
              onChange={set("deductions")}
              helperText="Itemized deductions exceeding standard deduction"
            />
          </Box>
          <MoneyField
            label="Step 4c — Extra withholding / pay period"
            size="small"
            fullWidth
            value={form.extra_withholding}
            onChange={set("extra_withholding")}
            helperText="Additional amount withheld from each paycheck"
            sx={{ mt: 2 }}
          />
        </Box>

        {/* Exempt */}
        <FormControlLabel
          control={
            <Switch
              checked={form.exempt}
              onChange={(e) => setForm((f) => ({ ...f, exempt: e.target.checked }))}
              size="small"
              color="warning"
            />
          }
          label={
            <Typography variant="body2">
              Exempt from federal withholding (W-4 line 4 — check only if no tax liability
              last year AND none expected this year)
            </Typography>
          }
        />

        <Divider />

        {/* Work state + effective date */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <FormControl size="small">
            <InputLabel>Primary Work State</InputLabel>
            <Select
              value={form.work_state}
              label="Primary Work State"
              onChange={(e) => setForm((f) => ({ ...f, work_state: e.target.value }))}
            >
              {US_STATES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Effective Date"
            type="date"
            size="small"
            value={form.effective_date}
            onChange={set("effective_date")}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Divider />

        {/* Employer info for PDF */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Employer information — printed on the generated PDF
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 2 }}>
            <TextField
              label="Employer Name"
              size="small"
              value={form.employer_name}
              onChange={set("employer_name")}
            />
            <TextField
              label="EIN (XX-XXXXXXX)"
              size="small"
              value={form.employer_ein}
              onChange={set("employer_ein")}
              inputProps={{ maxLength: 11 }}
            />
          </Box>
        </Box>

        {/* Error */}
        {submitMut.isError && (
          <Alert severity="error">
            {String((submitMut.error as Error)?.message)}
          </Alert>
        )}

        {/* Success */}
        {submitted && (
          <Alert
            severity="success"
            action={
              <Button
                size="small"
                color="inherit"
                startIcon={<DownloadIcon />}
                href={submitted.document_url}
                target="_blank"
                rel="noopener"
              >
                Download PDF
              </Button>
            }
          >
            W-4 saved — effective {submitted.effective_date}. PDF generated and stored for
            audit retention.
          </Alert>
        )}

        {/* Submit */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            variant="contained"
            onClick={() => submitMut.mutate()}
            disabled={submitMut.isPending}
            sx={{ textTransform: "none" }}
          >
            {submitMut.isPending
              ? "Generating…"
              : currentW4
              ? "Update W-4 + Regenerate PDF"
              : "Submit W-4 + Generate PDF"}
          </Button>
          <Link to="/payroll/employees/$employeeId" params={{ employeeId }}>
            <Button variant="text" sx={{ textTransform: "none" }}>
              Cancel
            </Button>
          </Link>
        </Box>
      </Paper>
    </Box>
  );
}
